import { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  MoreHorizontal,
  Edit,
  Trash2,
  ClipboardList,
  Ruler,
  Package,
  Hash,
  FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { yardApi, YardPipeTally as YardPipeTallyType, YardPipeJoint } from '@/api/services/yard.api';
import { locationsApi } from '@/api/services/locations.api';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';

type PipeGrade = 'API_5CT_J55' | 'API_5CT_K55' | 'API_5CT_L80' | 'API_5CT_N80' | 'API_5CT_C90' | 'API_5CT_T95' | 'API_5CT_P110' | 'API_5CT_Q125' | 'OTHER';
type PipeCondition = 'NEW' | 'PREMIUM' | 'CLASS_2' | 'CLASS_3' | 'SCRAP';

const PIPE_GRADES: { value: PipeGrade; label: string }[] = [
  { value: 'API_5CT_J55', label: 'J55' },
  { value: 'API_5CT_K55', label: 'K55' },
  { value: 'API_5CT_L80', label: 'L80' },
  { value: 'API_5CT_N80', label: 'N80' },
  { value: 'API_5CT_C90', label: 'C90' },
  { value: 'API_5CT_T95', label: 'T95' },
  { value: 'API_5CT_P110', label: 'P110' },
  { value: 'API_5CT_Q125', label: 'Q125' },
  { value: 'OTHER', label: 'Other' },
];

const PIPE_CONDITIONS: { value: PipeCondition; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'PREMIUM', label: 'Premium' },
  { value: 'CLASS_2', label: 'Class 2' },
  { value: 'CLASS_3', label: 'Class 3' },
  { value: 'SCRAP', label: 'Scrap' },
];

const PIPE_TYPES = [
  { value: 'CASING', label: 'Casing' },
  { value: 'TUBING', label: 'Tubing' },
  { value: 'DRILL_PIPE', label: 'Drill Pipe' },
  { value: 'LINE_PIPE', label: 'Line Pipe' },
];

export default function YardPipeTallyPage() {
  const [tallies, setTallies] = useState<YardPipeTallyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTallies: 0,
    totalJoints: 0,
    totalFootage: 0,
    inStock: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [pipeTypeFilter, setPipeTypeFilter] = useState<string>('all');
  const [gradeFilter, setGradeFilter] = useState<string>('all');

  // Locations for form
  const [locations, setLocations] = useState<any[]>([]);

  // Create/Edit dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTally, setEditingTally] = useState<YardPipeTallyType | null>(null);
  const [formData, setFormData] = useState({
    locationId: '',
    pipeType: '',
    outerDiameter: '',
    wallThickness: '',
    weight: '',
    grade: '' as PipeGrade | '',
    connection: '',
    range: '',
    condition: 'NEW' as PipeCondition,
    heatNumber: '',
    millName: '',
    storageLocation: '',
    notes: '',
  });

  // Tally details sheet
  const [selectedTally, setSelectedTally] = useState<YardPipeTallyType | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Add joints dialog
  const [isAddJointsDialogOpen, setIsAddJointsDialogOpen] = useState(false);
  const [tallyForJoints, setTallyForJoints] = useState<YardPipeTallyType | null>(null);
  const [jointsFormData, setJointsFormData] = useState({
    joints: [{ length: '', serialNumber: '', condition: 'NEW' as PipeCondition }],
  });

  // Perform tally dialog
  const [isTallyDialogOpen, setIsTallyDialogOpen] = useState(false);
  const [tallyToCount, setTallyToCount] = useState<YardPipeTallyType | null>(null);
  const [tallyCountData, setTallyCountData] = useState({
    joints: '',
    totalFootage: '',
    notes: '',
  });

  useEffect(() => {
    fetchTallies();
    fetchStats();
    fetchLocations();
  }, [pipeTypeFilter, gradeFilter]);

  const fetchTallies = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (pipeTypeFilter !== 'all') params.pipeType = pipeTypeFilter;
      if (gradeFilter !== 'all') params.grade = gradeFilter;

      const response = await yardApi.pipeTally.getAll(params);
      setTallies(response);
    } catch (error) {
      console.error('Error fetching pipe tallies:', error);
      toast.error('Failed to load pipe tallies');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await yardApi.pipeTally.getSummary();
      setStats(response);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationsApi.getAll();
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleCreateTally = async () => {
    if (!formData.locationId || !formData.pipeType || !formData.grade) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        locationId: formData.locationId,
        pipeType: formData.pipeType,
        outerDiameter: parseFloat(formData.outerDiameter),
        wallThickness: parseFloat(formData.wallThickness),
        weight: parseFloat(formData.weight),
        grade: formData.grade as PipeGrade,
        connection: formData.connection || undefined,
        range: formData.range || undefined,
        condition: formData.condition,
        heatNumber: formData.heatNumber || undefined,
        millName: formData.millName || undefined,
        storageLocation: formData.storageLocation || undefined,
        notes: formData.notes || undefined,
      };

      if (editingTally) {
        await yardApi.pipeTally.update(editingTally.id, payload);
        toast.success('Pipe tally updated');
      } else {
        await yardApi.pipeTally.create(payload);
        toast.success('Pipe tally created');
      }

      setIsCreateDialogOpen(false);
      setEditingTally(null);
      resetForm();
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error saving pipe tally:', error);
      toast.error('Failed to save pipe tally');
    }
  };

  const handleDeleteTally = async (tally: YardPipeTallyType) => {
    if (!confirm('Are you sure you want to delete this pipe tally?')) return;

    try {
      await yardApi.pipeTally.delete(tally.id);
      toast.success('Pipe tally deleted');
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error deleting pipe tally:', error);
      toast.error('Failed to delete pipe tally');
    }
  };

  const handleAddJoints = async () => {
    if (!tallyForJoints) return;

    const validJoints = jointsFormData.joints.filter(j => j.length);
    if (validJoints.length === 0) {
      toast.error('Please add at least one joint');
      return;
    }

    try {
      await yardApi.pipeTally.addJoints(tallyForJoints.id, validJoints.map(j => ({
        length: parseFloat(j.length),
        serialNumber: j.serialNumber || undefined,
        condition: j.condition,
      })));
      toast.success('Joints added successfully');
      setIsAddJointsDialogOpen(false);
      setTallyForJoints(null);
      setJointsFormData({ joints: [{ length: '', serialNumber: '', condition: 'NEW' }] });
      fetchTallies();
      fetchStats();

      // Refresh selected tally if viewing
      if (selectedTally?.id === tallyForJoints.id) {
        const response = await yardApi.pipeTally.getById(tallyForJoints.id);
        setSelectedTally(response);
      }
    } catch (error) {
      console.error('Error adding joints:', error);
      toast.error('Failed to add joints');
    }
  };

  const handlePerformTally = async () => {
    if (!tallyToCount) return;

    try {
      await yardApi.pipeTally.performTally(tallyToCount.id, {
        joints: parseInt(tallyCountData.joints),
        totalFootage: parseFloat(tallyCountData.totalFootage),
        notes: tallyCountData.notes || undefined,
      });
      toast.success('Tally count updated');
      setIsTallyDialogOpen(false);
      setTallyToCount(null);
      setTallyCountData({ joints: '', totalFootage: '', notes: '' });
      fetchTallies();
      fetchStats();
    } catch (error) {
      console.error('Error performing tally:', error);
      toast.error('Failed to update tally');
    }
  };

  const openEditDialog = (tally: YardPipeTallyType) => {
    setEditingTally(tally);
    setFormData({
      locationId: tally.locationId,
      pipeType: tally.pipeType,
      outerDiameter: tally.outerDiameter.toString(),
      wallThickness: tally.wallThickness.toString(),
      weight: tally.weight.toString(),
      grade: tally.grade as PipeGrade,
      connection: tally.connection || '',
      range: tally.range || '',
      condition: tally.condition as PipeCondition,
      heatNumber: tally.heatNumber || '',
      millName: tally.millName || '',
      storageLocation: tally.storageLocation || '',
      notes: tally.notes || '',
    });
    setIsCreateDialogOpen(true);
  };

  const openAddJointsDialog = (tally: YardPipeTallyType) => {
    setTallyForJoints(tally);
    setJointsFormData({ joints: [{ length: '', serialNumber: '', condition: 'NEW' }] });
    setIsAddJointsDialogOpen(true);
  };

  const openTallyDialog = (tally: YardPipeTallyType) => {
    setTallyToCount(tally);
    setTallyCountData({
      joints: tally.joints.toString(),
      totalFootage: tally.totalFootage.toString(),
      notes: '',
    });
    setIsTallyDialogOpen(true);
  };

  const viewTallyDetails = async (tally: YardPipeTallyType) => {
    try {
      const response = await yardApi.pipeTally.getById(tally.id);
      setSelectedTally(response);
      setIsSheetOpen(true);
    } catch (error) {
      console.error('Error fetching tally details:', error);
      toast.error('Failed to load tally details');
    }
  };

  const resetForm = () => {
    setFormData({
      locationId: '',
      pipeType: '',
      outerDiameter: '',
      wallThickness: '',
      weight: '',
      grade: '',
      connection: '',
      range: '',
      condition: 'NEW',
      heatNumber: '',
      millName: '',
      storageLocation: '',
      notes: '',
    });
  };

  const addJointRow = () => {
    setJointsFormData(prev => ({
      joints: [...prev.joints, { length: '', serialNumber: '', condition: 'NEW' as PipeCondition }],
    }));
  };

  const removeJointRow = (index: number) => {
    setJointsFormData(prev => ({
      joints: prev.joints.filter((_, i) => i !== index),
    }));
  };

  const updateJointRow = (index: number, field: string, value: string) => {
    setJointsFormData(prev => ({
      joints: prev.joints.map((j, i) => (i === index ? { ...j, [field]: value } : j)),
    }));
  };

  const getConditionBadge = (condition: PipeCondition) => {
    const config: Record<PipeCondition, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      NEW: { variant: 'default', label: 'New' },
      PREMIUM: { variant: 'secondary', label: 'Premium' },
      CLASS_2: { variant: 'outline', label: 'Class 2' },
      CLASS_3: { variant: 'outline', label: 'Class 3' },
      SCRAP: { variant: 'destructive', label: 'Scrap' },
    };
    const { variant, label } = config[condition] || { variant: 'secondary', label: condition };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getGradeLabel = (grade: PipeGrade) => {
    return PIPE_GRADES.find(g => g.value === grade)?.label || grade;
  };

  const filteredTallies = tallies.filter(tally => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesNumber = tally.tallyNumber?.toLowerCase().includes(query);
      const matchesHeat = tally.heatNumber?.toLowerCase().includes(query);
      const matchesMill = tally.millName?.toLowerCase().includes(query);
      if (!matchesNumber && !matchesHeat && !matchesMill) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Pipe Tally"
        description="Manage pipe inventory and tallies"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { fetchTallies(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => { resetForm(); setEditingTally(null); setIsCreateDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              New Pipe Tally
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Tallies"
          value={stats.totalTallies}
          icon={<FileText className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Joints"
          value={stats.totalJoints.toLocaleString()}
          icon={<Hash className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Footage"
          value={`${stats.totalFootage.toLocaleString()} ft`}
          icon={<Ruler className="h-5 w-5" />}
        />
        <MetricCard
          label="In Stock"
          value={stats.inStock}
          icon={<Package className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by tally number, heat number, or mill..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-md"
          />
        </div>
        <div className="flex gap-2">
          <Select value={pipeTypeFilter} onValueChange={setPipeTypeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Pipe Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {PIPE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={gradeFilter} onValueChange={setGradeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Grade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Grades</SelectItem>
              {PIPE_GRADES.map(grade => (
                <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tallies Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tally #</TableHead>
                <TableHead>Type / Grade</TableHead>
                <TableHead>OD x Wall</TableHead>
                <TableHead>Joints</TableHead>
                <TableHead>Footage</TableHead>
                <TableHead>Condition</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading pipe tallies...
                  </TableCell>
                </TableRow>
              ) : filteredTallies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No pipe tallies found
                  </TableCell>
                </TableRow>
              ) : (
                filteredTallies.map((tally) => (
                  <TableRow key={tally.id}>
                    <TableCell className="font-medium">{tally.tallyNumber}</TableCell>
                    <TableCell>
                      <div>
                        <p>{PIPE_TYPES.find(t => t.value === tally.pipeType)?.label || tally.pipeType}</p>
                        <Badge variant="outline" className="mt-1">
                          {getGradeLabel(tally.grade as PipeGrade)}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {tally.outerDiameter}" x {tally.wallThickness}"
                      <p className="text-sm text-muted-foreground">{tally.weight} lb/ft</p>
                    </TableCell>
                    <TableCell>{tally.joints.toLocaleString()}</TableCell>
                    <TableCell>{tally.totalFootage.toLocaleString()} ft</TableCell>
                    <TableCell>{getConditionBadge(tally.condition as PipeCondition)}</TableCell>
                    <TableCell>
                      <Badge variant={tally.status === 'IN_STOCK' ? 'default' : 'secondary'}>
                        {tally.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewTallyDetails(tally)}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(tally)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAddJointsDialog(tally)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Joints
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openTallyDialog(tally)}>
                            <ClipboardList className="h-4 w-4 mr-2" />
                            Perform Tally
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDeleteTally(tally)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTally ? 'Edit Pipe Tally' : 'New Pipe Tally'}</DialogTitle>
            <DialogDescription>
              {editingTally ? 'Update pipe tally information.' : 'Create a new pipe tally record.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="locationId">Location *</Label>
                <Select
                  value={formData.locationId}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, locationId: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pipeType">Pipe Type *</Label>
                <Select
                  value={formData.pipeType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, pipeType: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="outerDiameter">OD (inches) *</Label>
                <Input
                  id="outerDiameter"
                  type="number"
                  step="0.001"
                  value={formData.outerDiameter}
                  onChange={(e) => setFormData(prev => ({ ...prev, outerDiameter: e.target.value }))}
                  placeholder="e.g., 7.0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wallThickness">Wall (inches) *</Label>
                <Input
                  id="wallThickness"
                  type="number"
                  step="0.001"
                  value={formData.wallThickness}
                  onChange={(e) => setFormData(prev => ({ ...prev, wallThickness: e.target.value }))}
                  placeholder="e.g., 0.408"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight (lb/ft) *</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.01"
                  value={formData.weight}
                  onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
                  placeholder="e.g., 29.0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="grade">Grade *</Label>
                <Select
                  value={formData.grade}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, grade: value as PipeGrade }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPE_GRADES.map(grade => (
                      <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="condition">Condition</Label>
                <Select
                  value={formData.condition}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, condition: value as PipeCondition }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPE_CONDITIONS.map(cond => (
                      <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="connection">Connection Type</Label>
                <Input
                  id="connection"
                  value={formData.connection}
                  onChange={(e) => setFormData(prev => ({ ...prev, connection: e.target.value }))}
                  placeholder="e.g., BTC, LTC, STC"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="range">Range</Label>
                <Select
                  value={formData.range}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, range: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="R1">R1 (18-22 ft)</SelectItem>
                    <SelectItem value="R2">R2 (28-32 ft)</SelectItem>
                    <SelectItem value="R3">R3 (38-45 ft)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="heatNumber">Heat Number</Label>
                <Input
                  id="heatNumber"
                  value={formData.heatNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, heatNumber: e.target.value }))}
                  placeholder="Mill heat number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="millName">Mill Name</Label>
                <Input
                  id="millName"
                  value={formData.millName}
                  onChange={(e) => setFormData(prev => ({ ...prev, millName: e.target.value }))}
                  placeholder="e.g., Tenaris, Vallourec"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="storageLocation">Storage Location</Label>
              <Input
                id="storageLocation"
                value={formData.storageLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, storageLocation: e.target.value }))}
                placeholder="Yard location reference"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTally}>
              {editingTally ? 'Update' : 'Create'} Tally
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Joints Dialog */}
      <Dialog open={isAddJointsDialogOpen} onOpenChange={setIsAddJointsDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Add Joints</DialogTitle>
            <DialogDescription>
              Add individual pipe joints to this tally.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[50vh] overflow-y-auto space-y-3">
            {jointsFormData.joints.map((joint, index) => (
              <div key={index} className="flex items-end gap-2 p-3 bg-muted/50 rounded-lg">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Length (ft)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={joint.length}
                    onChange={(e) => updateJointRow(index, 'length', e.target.value)}
                    placeholder="Length"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Serial #</Label>
                  <Input
                    value={joint.serialNumber}
                    onChange={(e) => updateJointRow(index, 'serialNumber', e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="w-28 space-y-1">
                  <Label className="text-xs">Condition</Label>
                  <Select
                    value={joint.condition}
                    onValueChange={(value) => updateJointRow(index, 'condition', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PIPE_CONDITIONS.map(cond => (
                        <SelectItem key={cond.value} value={cond.value}>{cond.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {jointsFormData.joints.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeJointRow(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" className="w-full" onClick={addJointRow}>
              <Plus className="h-4 w-4 mr-2" />
              Add Another Joint
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddJointsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddJoints}>Add Joints</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Perform Tally Dialog */}
      <Dialog open={isTallyDialogOpen} onOpenChange={setIsTallyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Perform Tally Count</DialogTitle>
            <DialogDescription>
              Update the joint count and footage for this pipe tally.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="joints">Joint Count</Label>
              <Input
                id="joints"
                type="number"
                value={tallyCountData.joints}
                onChange={(e) => setTallyCountData(prev => ({ ...prev, joints: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalFootage">Total Footage (ft)</Label>
              <Input
                id="totalFootage"
                type="number"
                step="0.01"
                value={tallyCountData.totalFootage}
                onChange={(e) => setTallyCountData(prev => ({ ...prev, totalFootage: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tallyNotes">Notes</Label>
              <Textarea
                id="tallyNotes"
                value={tallyCountData.notes}
                onChange={(e) => setTallyCountData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Tally notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTallyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePerformTally}>
              <ClipboardList className="h-4 w-4 mr-2" />
              Update Tally
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tally Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Pipe Tally Details</SheetTitle>
            <SheetDescription>
              View complete tally information
            </SheetDescription>
          </SheetHeader>
          {selectedTally && (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tally Number</p>
                  <p className="font-medium">{selectedTally.tallyNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={selectedTally.status === 'IN_STOCK' ? 'default' : 'secondary'}>
                    {selectedTally.status}
                  </Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Pipe Specifications</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>{' '}
                    {PIPE_TYPES.find(t => t.value === selectedTally.pipeType)?.label}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Grade:</span>{' '}
                    {getGradeLabel(selectedTally.grade as PipeGrade)}
                  </div>
                  <div>
                    <span className="text-muted-foreground">OD:</span>{' '}
                    {selectedTally.outerDiameter}"
                  </div>
                  <div>
                    <span className="text-muted-foreground">Wall:</span>{' '}
                    {selectedTally.wallThickness}"
                  </div>
                  <div>
                    <span className="text-muted-foreground">Weight:</span>{' '}
                    {selectedTally.weight} lb/ft
                  </div>
                  <div>
                    <span className="text-muted-foreground">Condition:</span>{' '}
                    {getConditionBadge(selectedTally.condition as PipeCondition)}
                  </div>
                  {selectedTally.connection && (
                    <div>
                      <span className="text-muted-foreground">Connection:</span>{' '}
                      {selectedTally.connection}
                    </div>
                  )}
                  {selectedTally.range && (
                    <div>
                      <span className="text-muted-foreground">Range:</span>{' '}
                      {selectedTally.range}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Inventory</h4>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4">
                    <p className="text-2xl font-bold">{selectedTally.joints.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Joints</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-2xl font-bold">{selectedTally.totalFootage.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Footage (ft)</p>
                  </Card>
                </div>
                {selectedTally.avgJointLength && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Avg Joint Length: {selectedTally.avgJointLength.toFixed(2)} ft
                  </p>
                )}
              </div>

              {selectedTally.jointsDetail && selectedTally.jointsDetail.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">
                    Joint Details ({selectedTally.jointsDetail.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {selectedTally.jointsDetail.map((joint: YardPipeJoint) => (
                      <div
                        key={joint.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm"
                      >
                        <span>Joint #{joint.jointNumber}</span>
                        <span>{joint.length} ft</span>
                        {getConditionBadge(joint.condition as PipeCondition)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedTally.heatNumber || selectedTally.millName) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Mill Information</h4>
                  <div className="space-y-2 text-sm">
                    {selectedTally.heatNumber && (
                      <div>
                        <span className="text-muted-foreground">Heat Number:</span>{' '}
                        {selectedTally.heatNumber}
                      </div>
                    )}
                    {selectedTally.millName && (
                      <div>
                        <span className="text-muted-foreground">Mill:</span>{' '}
                        {selectedTally.millName}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
